import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "utils";
import { ProficiencyCell, CollabHex } from "../shared/QuestionMetrics";

// Width budget for the chunking math (keep in sync with the styled widths
// below): each question column is CELL_W wide, the left label column LABEL_W.
const CELL_W = 32;
const LABEL_W = 104;

// Splits an array into chunks of `size`.
const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const isUnfinished = (item) => item?.loop_unfinished === true;

// Per-question Accuracy + Collaboration grid for a completed/analyzing
// session, mirroring the live pill's Live Updates rows. Figma 2461:31005 /
// 32078. `challenges` is the session_history_challenges array; index = question
// order. Collapsed shows the first row of questions; "Show all" reveals the
// rest, wrapped into label-prefixed blocks.
export const QuestionSummary = ({ challenges, onSelectQuestion }) => {
  const containerRef = useRef(null);
  const [perRow, setPerRow] = useState(18);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const width = el.clientWidth;
      const next = Math.max(1, Math.floor((width - LABEL_W) / CELL_W));
      setPerRow(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const items = useMemo(
    () => (Array.isArray(challenges) ? challenges : []),
    [challenges],
  );

  if (items.length === 0) return null;

  const rows = chunk(items, perRow);
  const visibleRows = expanded ? rows : rows.slice(0, 1);
  const hasMore = rows.length > 1;

  return (
    <Wrapper>
      <Header>
        <Heading>Group Results</Heading>
        {hasMore && (
          <ShowAll
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? "Show less" : "Show all"}
            <Chevron $up={expanded} aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Chevron>
          </ShowAll>
        )}
      </Header>

      <Grid ref={containerRef}>
        {visibleRows.map((row, rowIdx) => (
          <Block key={rowIdx}>
            <BlockRow>
              <RowLabel>Question</RowLabel>
              <Cells>
                {row.map((item, i) => {
                  const globalIndex = rowIdx * perRow + i;
                  return (
                    <Cell key={item?.challenge?.id ?? globalIndex}>
                      {/* Underlined blue text that does nothing promises a jump
                          the layout cannot make, so without a handler the
                          number is just a number. */}
                      {onSelectQuestion ? (
                        <QNum
                          type="button"
                          onClick={() => onSelectQuestion(globalIndex)}
                        >
                          {globalIndex + 1}
                        </QNum>
                      ) : (
                        <QNumPlain>{globalIndex + 1}</QNumPlain>
                      )}
                    </Cell>
                  );
                })}
              </Cells>
            </BlockRow>
            <BlockRow>
              <RowLabel>Accuracy</RowLabel>
              <Cells>
                {row.map((item, i) => (
                  <Cell key={item?.challenge?.id ?? `p-${rowIdx}-${i}`}>
                    <ProficiencyCell
                      aligned={item?.proficiency_aligned}
                      unfinished={isUnfinished(item)}
                    />
                  </Cell>
                ))}
              </Cells>
            </BlockRow>
            <BlockRow>
              <RowLabel>Collaboration</RowLabel>
              <Cells>
                {row.map((item, i) => (
                  <Cell key={item?.challenge?.id ?? `c-${rowIdx}-${i}`}>
                    {/* Match the live pill: a null level renders the N/A hex. */}
                    <CollabHex
                      level={item?.collaboration_level}
                      na={item?.collaboration_level == null}
                      unfinished={isUnfinished(item)}
                    />
                  </Cell>
                ))}
              </Cells>
            </BlockRow>
          </Block>
        ))}
      </Grid>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-bottom: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const Heading = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: ${colors.nightlyWoods950};
`;

const ShowAll = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: ${colors.blue500};
  &:hover { opacity: 0.8; }
`;

const Chevron = styled.span`
  display: inline-flex;
  transition: transform 120ms ease;
  transform: rotate(${({ $up }) => ($up ? "180deg" : "0deg")});
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BlockRow = styled.div`
  display: flex;
  align-items: center;
`;

const RowLabel = styled.span`
  width: ${LABEL_W}px;
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: ${colors.nightlyWoods950};
`;

const Cells = styled.div`
  display: flex;
  align-items: center;
`;

const Cell = styled.div`
  width: ${CELL_W}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const QNum = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.blue500};
  text-decoration: underline;
  text-underline-position: from-font;
  &:hover { opacity: 0.75; }
`;

const QNumPlain = styled.span`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${colors.nightlyWoods950};
`;

export default QuestionSummary;
